import React, { useEffect, useMemo } from 'react';
import { useQueryLoader } from 'react-relay';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import uiJiraSoftwareEditSprintModalQuery, {
	type uiJiraSoftwareEditSprintModalQuery as uiJiraSoftwareEditSprintModalQueryType,
} from '@atlassian/jira-relay/src/__generated__/uiJiraSoftwareEditSprintModalQuery.graphql';
import EditSprintModal from '@atlassian/jira-software-edit-sprint-modal-relay/async.tsx';
import type { OnSubmit } from '@atlassian/jira-software-edit-sprint-modal-relay/src/common/types.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	editSprintRequest,
	editSprintSuccess,
	editSprintFailure,
} from '../../../state/actions/sprints/edit/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../state/index.tsx';
import { getRapidViewId } from '../../../state/selectors/app/index.tsx';
import { useIsCMPBacklog } from '../../../state/state-hooks/capabilities.tsx';

type Props = {
	cardListId: number;
	onClose: () => void;
	onSubmit: OnSubmit;
};

const EditSprintModalRelay = (props: Props) => {
	const { cardListId, ...rest } = props;
	const boardId = useBacklogSelector((state) => Number(getRapidViewId(state)));
	const cloudId = useCloudId();
	const isCompanyManaged = useIsCMPBacklog();
	const [queryReference, loadQuery] = useQueryLoader<uiJiraSoftwareEditSprintModalQueryType>(
		uiJiraSoftwareEditSprintModalQuery,
	);
	const boardAri = useMemo(
		() =>
			createAri({
				resourceOwner: 'jira-software',
				cloudId,
				resourceType: 'board',
				resourceId: String(boardId),
			}),
		[boardId, cloudId],
	);

	const sprintAri = useMemo(
		() =>
			createAri({
				resourceOwner: 'jira-software',
				cloudId,
				resourceType: 'sprint',
				resourceId: String(cardListId),
			}),
		[cloudId, cardListId],
	);
	useEffect(() => {
		loadQuery(
			{
				boardId: boardAri,
				sprintId: sprintAri,
				fetchSprints: false,
			},
			{
				fetchPolicy: 'network-only',
			},
		);
	}, [boardAri, loadQuery, sprintAri]);

	const dispatch = useBacklogDispatch();

	const onSubmit: Props['onSubmit'] = (data, submitPromise) => {
		props.onSubmit(data, submitPromise);

		const { name, goal, startDate, endDate } = data;

		const action = editSprintRequest(cardListId, name, goal, startDate, endDate);
		const {
			meta: {
				optimistic: { id: optimisticId },
			},
		} = action;
		dispatch(action);

		submitPromise
			.then(() => {
				dispatch(editSprintSuccess(optimisticId));
			})
			.catch(() => {
				dispatch(editSprintFailure(optimisticId));
			});
	};

	return (
		<EditSprintModal
			{...rest}
			onSubmit={onSubmit}
			boardAri={boardAri}
			queryReference={queryReference}
			isCompanyManaged={isCompanyManaged}
		/>
	);
};

export default EditSprintModalRelay;
